import React, { useState, useEffect, FormEvent } from "react";
import PageHeader from "src/Components/Customer/PageHeader";
import PreviewProfileButton from "src/Components/Customer/PreviewProfileButton";
import SimpleInput from "src/Components/SimpleInput";
import ToggleRadioBtn from "src/Components/ToggleRadioBtn";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";

import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IAvailability } from "src/types/GlobalInterfaces";
import { weekDays } from "src/lib/constans";
import { capitalize } from "@mui/material";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { updateDoctorField } from "../../../Store/doctor";
import { setUniqueData } from "src/Store/reducer";

const OnlineAvailabilityEdit: React.FC = () => {
  const { profile } = useSelector((store: RootState) => store.doctor);
  const dispatch = useDispatch();
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [isTerms, setIsTerms] = useState<any>(true);
  const [availability, setAvailability] = useState<IAvailability>({
    fees: "",
  });

  const handleToggle = (day: keyof IAvailability) => {
    const isExist = Object.keys(availability).includes(day);
    if (isExist) {
      setAvailability((prev) => {
        const updatedAvailability = { ...prev } as Record<string, any>; // Assert type
        delete updatedAvailability[day];
        return updatedAvailability;
      }); // Remove the day from the availability object
    } else {
      setAvailability((prev) => ({
        ...prev,
        [day]: [["", ""]],
      }));
    }
  };

  const handleChange = (
    day: keyof IAvailability,
    index: number,
    type: number,
    value: string
  ) => {
    setAvailability((prev) => {
      const currentSchedule = Array.isArray(prev[day])
        ? [...(prev[day] as [string, string][])]
        : [];
      const updatedSlots = currentSchedule.map((slot, slotIdx) =>
        slotIdx === index ? [...slot] : slot
      );
      updatedSlots[index][type] = value;

      // Function to convert time string to number of minutes
      const timeToMinutes = (time: string): number => {
        if (!time) return 0;
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      // Function to convert minutes to 12-hour format (hh:mm A)
      const minutesTo12HourFormat = (minutes: number): string => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const period = hours >= 12 ? "PM" : "AM";
        const hour12 = hours % 12 || 12;
        return `${hour12}:${mins.toString().padStart(2, "0")} ${period}`;
      };

      // Helper function to get intervals for a slot (handling wrap to midnight only)
      const getIntervals = (fromMin: number, toMin: number): [number, number][] => {
        if (toMin > fromMin) {
          return [[fromMin, toMin]]; // Normal same-day
        } else if (toMin === 0 && fromMin > 0) {
          return [[fromMin, 1440]]; // Wrap to exactly midnight (end at 24:00)
        } else {
          return []; // Invalid wrap
        }
      };

      // Helper function to check if two sets of intervals overlap
      const checkOverlap = (
        newIntervals: [number, number][],
        otherIntervals: [number, number][]
      ): boolean => {
        for (const ni of newIntervals) {
          for (const oi of otherIntervals) {
            if (Math.max(ni[0], oi[0]) < Math.min(ni[1], oi[1])) {
              return true;
            }
          }
        }
        return false;
      };

      // Check if 'To' time is valid relative to 'From' time
      const fromTime = updatedSlots[index][0];
      const toTime = updatedSlots[index][1];

      if (fromTime && toTime) {
        const fromMinutes = timeToMinutes(fromTime);
        const toMinutes = timeToMinutes(toTime);
        
        if (toMinutes === fromMinutes) {
          toast(
            `${capitalize(
              String(day)
            )}: Start and end times must be different.`,
            {
              type: "error",
            }
          );
          const prevState = currentSchedule.map((slot) => [...slot]);
          prevState[index][type] = "";
          return { ...prev, [day]: prevState };
        } else if (toMinutes < fromMinutes) {
          // Wrap-around only allowed if end is exactly 12:00 AM (00:00)
          if (toMinutes !== 0) {
            toast(
              `${capitalize(
                String(day)
              )}: For overnight shifts, end time can only be up to 12:00 AM.`,
              {
                type: "error",
              }
            );
            const prevState = currentSchedule.map((slot) => [...slot]);
            prevState[index][type] = "";
            return { ...prev, [day]: prevState };
          }
        }
        // Normal case (to > from) or valid wrap (to=0 < from) is fine here
      }

      // Validate: Check for overlapping time slots within the same day
      if (fromTime && toTime) {
        const newFrom = timeToMinutes(fromTime);
        const newTo = timeToMinutes(toTime);
        const newIntervals = getIntervals(newFrom, newTo);
        if (newIntervals.length === 0) {
          // Shouldn't reach here due to earlier validation, but safety
          const prevState = currentSchedule.map((slot) => [...slot]);
          prevState[index][type] = "";
          return { ...prev, [day]: prevState };
        }

        for (let i = 0; i < updatedSlots.length; i++) {
          if (i === index) continue;
          const [otherFromStr, otherToStr] = updatedSlots[i];
          if (otherFromStr && otherToStr) {
            const otherFromMinutes = timeToMinutes(otherFromStr);
            const otherToMinutes = timeToMinutes(otherToStr);
            const otherIntervals = getIntervals(otherFromMinutes, otherToMinutes);
            if (otherIntervals.length > 0 && checkOverlap(newIntervals, otherIntervals)) {
              const formattedOtherFrom = minutesTo12HourFormat(otherFromMinutes);
              const formattedOtherTo = minutesTo12HourFormat(otherToMinutes);
              toast(
                `${capitalize(
                  String(day)
                )}: Time slot overlaps with another slot (${formattedOtherFrom} - ${formattedOtherTo}).`,
                { type: "error" }
              );
              const prevState = currentSchedule.map((slot) => [...slot]);
              prevState[index][type] = "";
              return { ...prev, [day]: prevState };
            }
          }
        }
      }

      // Validate: Check for conflicts with hospital timings
      if (fromTime && toTime && profile?.timings) {
        const newFrom = timeToMinutes(fromTime);
        const newTo = timeToMinutes(toTime);
        const newIntervals = getIntervals(newFrom, newTo);
        if (newIntervals.length === 0) return { ...prev, [day]: updatedSlots };

        for (const hospitalSchedule of profile.timings) {
          const hospitalTimings = hospitalSchedule[day];
          if (hospitalTimings && Array.isArray(hospitalTimings)) {
            for (const [hospitalFromStr, hospitalToStr] of hospitalTimings) {
              if (hospitalFromStr && hospitalToStr) {
                const hospitalFromMinutes = timeToMinutes(hospitalFromStr);
                const hospitalToMinutes = timeToMinutes(hospitalToStr);
                const hospitalIntervals = getIntervals(hospitalFromMinutes, hospitalToMinutes);
                if (hospitalIntervals.length > 0 && checkOverlap(newIntervals, hospitalIntervals)) {
                  const formattedHospitalFrom = minutesTo12HourFormat(hospitalFromMinutes);
                  const formattedHospitalTo = minutesTo12HourFormat(hospitalToMinutes);
                  toast(
                    `${capitalize(
                      String(day)
                    )}: Online time slot conflicts with hospital schedule (${formattedHospitalFrom} - ${formattedHospitalTo}) at ${
                      hospitalSchedule.hospitalName
                    }.`,
                    { type: "error" }
                  );
                  const prevState = currentSchedule.map((slot) => [...slot]);
                  prevState[index][type] = "";
                  return { ...prev, [day]: prevState };
                }
              }
            }
          }
        }
      }

      return { ...prev, [day]: updatedSlots };
    });
  };

  const addRow = (day: keyof IAvailability) => {
    setAvailability((prev) => {
      // Ensure `prev[day]` is an array or default to an empty array
      const currentSchedule = Array.isArray(prev[day])
        ? (prev[day] as [string, string][])
        : [];
      return {
        ...prev,
        [day]: [...currentSchedule, ["", ""]],
      };
    });
  };

  const deleteRow = (day: keyof IAvailability, index: number) => {
    setAvailability((prev) => {
      // Ensure `prev[day]` is an array or default to an empty array
      const currentSchedule = Array.isArray(prev[day])
        ? (prev[day] as [string, string][])
        : [];

      // Filter out the item at the specified index
      const updatedSchedule = currentSchedule.filter((_, i) => i !== index);

      return {
        ...prev,
        [day]: updatedSchedule,
      };
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isTerms) {
      toast(
        "You must agree to the platform commission deduction before proceeding."
      );
      return;
    }
    setIsLoader(true);
    const { res, err } = await httpRequest<{
      message: string;
      availability: IAvailability;
      percent: number;
    }>({
      method: "post",
      path: "/provider/update-consultation",
      params: availability,
    });
    if (res) {
      const { message, availability, percent } = res;
      toast("Online availability updated", { type: "success" });
      dispatch(updateDoctorField({ availability }));
      dispatch(
        setUniqueData({ key: "profileCompletion", value: String(percent) })
      );
    } else {
      toast(err, { type: "error" }); // api error
    }
    setIsLoader(false);
  };

  useEffect(() => {
    if (profile) setAvailability(profile.availability || {});
  }, [profile]);

  return (
    <>
      <PageHeader
        title="Availability"
        isButton={true}
        ButtonComponent={PreviewProfileButton}
      />

      <div className="parent-box">
        <div className="border-b border-[#707070] pt-2 pb-4">
          <p className="font-Poppins-SemiBold font-size-20px theme-color-green">
            Online availability days and times
          </p>
          <p className="font-Poppins-Regular font-size-14px theme-color-gren">
            Keep this up to date so you get enquiries that suit you.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="py-4">
          <div>
            <SimpleInput
              id="edit-online-availability-fees"
              type="text"
              value={availability?.fees || ""}
              onChange={(val) => {
                if (/^\d+$/.test(val) || val === "") {
                  // Allow only positive whole numbers
                  setAvailability((prev) => ({ ...prev, fees: val }));
                }
              }}
              isLabel={true}
              maxLength={6}
              labelText={"Charges"}
              isRequired={true}
              extraStyle={{ borderColor: "#009d91" }}
            />
          </div>
          {/* <div className="flex flex-row gap-1 items-center">
            <SimpleInput
              id="edit-online-availability-terms"
              type="checkbox"
              value={isTerms}
              onChange={(val) => {
                setIsTerms(val);
              }}
              isLabel={false}
              isRequired={true}
              extraStyle={{ borderColor: "#009d91", margin: 0 }}
            />
            <p className="font-size-14px font-Poppins-Regular ">
              By proceeding, you agree that the platform commission will be
              deducted from your set online appointment price.
            </p>
          </div> */}
          {weekDays.map((day) => (
            <div key={day} className="availability mt-4">
              <div className="avail pb-4">
                <div className="flex gap-3">
                  <ToggleRadioBtn
                    id={`edit-online-availability-toggle-${day}`}
                    toggleText={capitalize(day)}
                    isToggled={Object.keys(availability).includes(day)}
                    handleToggle={() =>
                      handleToggle(day as keyof IAvailability)
                    }
                  />
                </div>
                {Object.keys(availability).includes(day) && (
                  <>
                    {Array.isArray(availability[day as keyof IAvailability])
                      ? (
                          availability[day as keyof IAvailability] as [
                            string,
                            string
                          ][]
                        ).map((item, index) => (
                          <div
                            className="flex w-full gap-4 mt-4 items-center"
                            key={index}
                          >
                            <div className="w-[30%]">
                              <SimpleInput
                                id={`edit-online-availability-start-time-${day}-${index}`}
                                value={item[0]}
                                onChange={(val) =>
                                  handleChange(
                                    day as keyof IAvailability,
                                    index,
                                    0,
                                    val
                                  )
                                }
                                isLabel={true}
                                labelText={"Start Time"}
                                extraStyle={{ borderColor: "#009d91" }}
                                type="time"
                                isRequired={true}
                              />
                            </div>

                            <div className="w-[30%]">
                              <SimpleInput
                                id={`edit-online-availability-end-time-${day}-${index}`}
                                value={item[1]}
                                onChange={(val) =>
                                  handleChange(
                                    day as keyof IAvailability,
                                    index,
                                    1,
                                    val
                                  )
                                }
                                isLabel={true}
                                labelText={"End Time"}
                                extraStyle={{ borderColor: "#009d91" }}
                                type="time"
                                isRequired={true}
                              />
                            </div>
                            {index !== 0 && (
                              <button
                                id={`edit-online-availability-delete-btn-${day}-${index}`}
                                onClick={() =>
                                  deleteRow(day as keyof IAvailability, index)
                                }
                                type="button"
                                className="ml-4 mt-[35px]"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            )}
                          </div>
                        ))
                      : null}
                    <div className="mt-4">
                      <button
                        id={`edit-online-availability-add-btn-${day}`}
                        onClick={() => addRow(day as keyof IAvailability)}
                        type="button"
                        className="font-Poppins-semiBold font-size-18px theme-color-green"
                      >
                        + Add another time
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
          <div className="my-5">
            <PrimaryBtn
              id="edit-online-availability-save-btn"
              type="submit"
              loader={isLoader}
              ButtonText="Save"
              btnExtraStyle={{
                backgroundColor: "#004540",
                borderRadius: "50rem",
                marginLeft: "auto",
              }}
            />
          </div>
        </form>
      </div>
    </>
  );
};

export default OnlineAvailabilityEdit;